import { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { PAGE_BASE_WIDTH, renderPdfPages } from '../lib/pdf-render';
import { ZOOM_STEPS, ZoomControl } from './ZoomControl';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * Full-screen in-app PDF viewer - replaces the old target="_blank" links so
 * documents open INSIDE the portal. The bytes come through the API
 * (same-origin), rendered page by page with pdf.js; Download saves the same
 * bytes as a file without leaving the app.
 */
export function PdfViewerOverlay({
  title,
  subtitle,
  loadBytes,
  downloadName,
  beside,
  onClose,
}: {
  title: string;
  subtitle?: string;
  /** Fetches the PDF's bytes - called once on open. */
  loadBytes: () => Promise<ArrayBuffer>;
  /** When set, a Download button saves the bytes under this name. */
  downloadName?: string;
  /**
   * Show the viewer BESIDE the right-hand form drawer instead of over the
   * whole screen, so the form stays visible and usable while a file is
   * being checked. On a phone the drawer is full width, so it still covers.
   */
  beside?: boolean;
  onClose: () => void;
}) {
  const [pageCount, setPageCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageWidth = Math.round(PAGE_BASE_WIDTH * zoom);
  const bytesRef = useRef<ArrayBuffer | null>(null);
  const pdfRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const bytes = await loadBytes();
        // pdf.js consumes (detaches) the buffer it gets - keep a copy for
        // the Download button.
        bytesRef.current = bytes.slice(0);
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
      } catch {
        setFailed(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || pageCount === 0) return;
    const job = renderPdfPages(pdf, (n) => canvasRefs.current.get(n), pageWidth);
    return () => job.cancel();
  }, [pageCount, pageWidth]);

  // Open at the largest zoom that fits the space available - beside the form
  // drawer that is narrower than the full screen.
  useEffect(() => {
    if (pageCount === 0 || !scrollRef.current) return;
    const room = scrollRef.current.clientWidth - 48;
    const fitting = [...ZOOM_STEPS].reverse().find((z) => PAGE_BASE_WIDTH * z <= room);
    setZoom(Math.min(1, fitting ?? ZOOM_STEPS[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount > 0]);

  function download() {
    if (!bytesRef.current || !downloadName) return;
    const url = URL.createObjectURL(new Blob([bytesRef.current], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  return (
    <div className={[
      'fixed z-50 flex flex-col bg-slate-100',
      // 28rem = the form drawer's width (SidePanel's max-w-md).
      beside ? 'inset-y-0 left-0 right-0 md:right-[28rem]' : 'inset-0',
    ].join(' ')}>
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ZoomControl zoom={zoom} onChange={setZoom} />
          {downloadName && (
            <button
              type="button"
              onClick={download}
              disabled={!pageCount}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
            >
              Download
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto py-6">
        {failed && (
          <p className="py-16 text-center text-sm text-red-600">Could not load the PDF.</p>
        )}
        {pageCount === 0 && !failed && (
          <p className="py-16 text-center text-sm text-slate-500">Loading PDF…</p>
        )}
        <div className="mx-auto space-y-6" style={{ width: pageWidth }}>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <div key={n} className="rounded border border-slate-300 bg-white shadow-sm">
              <canvas ref={(el) => { if (el) canvasRefs.current.set(n, el); }} className="block" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
